import i18n from '@/locales/i18n';
import { v4 as uuidv4 } from 'uuid';
import { buildVueFlowEdgeId } from '@/libs/flow/flowClipboardEdge';
import { getNodeSourceHandleIds } from '@/views/flow/FlowGrap/nodeGrouping';
import { validateTextToImageConfig } from '@V/flow/FlowGrap/CustomNode/BlockNodeChild/NodeInfo/textToImageHelper';
export const NORMAL_EDGE_TYPE = 'normal';
export const ASYNC_EDGE_TYPE = 'async';
export const EXCEPTION_EDGE_TYPE = 'exception';
export const CANVAS_EDGE_TYPE = 'custom';
const API_EDGE_TYPES = new Set([NORMAL_EDGE_TYPE, ASYNC_EDGE_TYPE, EXCEPTION_EDGE_TYPE]);
export const resolveApiEdgeType = edge => {
  if (!edge) return NORMAL_EDGE_TYPE;
  if (edge.type === EXCEPTION_EDGE_TYPE || edge.data?.isExceptionBranch === true || edge.data?.saveEdgeType === EXCEPTION_EDGE_TYPE) {
    return EXCEPTION_EDGE_TYPE;
  }
  if (edge.type === ASYNC_EDGE_TYPE || edge.data?.isAsyncEdge === true || edge.data?.saveEdgeType === ASYNC_EDGE_TYPE) {
    return ASYNC_EDGE_TYPE;
  }
  if (edge.type === NORMAL_EDGE_TYPE || edge.data?.saveEdgeType === NORMAL_EDGE_TYPE) {
    return NORMAL_EDGE_TYPE;
  }
  return NORMAL_EDGE_TYPE;
};
const resolveNormalOrAsyncEdgeType = edge =>
  resolveApiEdgeType(edge) === ASYNC_EDGE_TYPE
    ? ASYNC_EDGE_TYPE
    : NORMAL_EDGE_TYPE;
export const buildCanvasEdgeData = (apiEdgeType, edgeData = {}) => {
  const nextData = {
    ...edgeData,
    saveEdgeType: apiEdgeType
  };
  if (apiEdgeType === EXCEPTION_EDGE_TYPE) {
    nextData.isExceptionBranch = true;
    delete nextData.isAsyncEdge;
  } else if (apiEdgeType === ASYNC_EDGE_TYPE) {
    nextData.isAsyncEdge = true;
    delete nextData.isExceptionBranch;
  } else {
    delete nextData.isExceptionBranch;
    delete nextData.isAsyncEdge;
  }
  return nextData;
};
export const OUTPUT_FORMAT_OPTIONS = [{
  value: 'text',
  label: i18n.global.t('views.flow.helper.exceptionHandling.7bcc38db75f34693aa746a144c6e0e89')
}, {
  value: 'markdown',
  label: 'Markdown'
}, {
  value: 'json',
  label: 'JSON'
}];
export const OUTPUT_FORMAT_TIP = i18n.global.t('views.flow.helper.exceptionHandling.8d0631a03f0d554132e04861b812c4ef');
export const DEFAULT_EXCEPTION_VALUE = {};
export const getDefaultExceptionHandling = () => ({
  enabled: false,
  execution_timeout_seconds: 60,
  first_token_timeout_enabled: true,
  first_token_timeout_seconds: 5,
  retry_times: 0,
  strategy: 'interrupt',
  default_value: {
    ...DEFAULT_EXCEPTION_VALUE
  },
  exception_branch_handle_id: uuidv4()
});
const parseDefaultValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {
        ...DEFAULT_EXCEPTION_VALUE
      };
    } catch {
      return {
        ...DEFAULT_EXCEPTION_VALUE
      };
    }
  }
  return {
    ...DEFAULT_EXCEPTION_VALUE
  };
};
const migrateLegacyExceptionConfig = legacy => {
  if (!legacy || typeof legacy !== 'object') {
    return null;
  }
  if ('enabled' in legacy || 'execution_timeout_seconds' in legacy || 'strategy' in legacy) {
    return legacy;
  }
  const strategy = legacy.exception_action === 'terminate' ? 'interrupt' : legacy.handle_type === 'default_assign' ? 'default_value' : legacy.handle_type || 'interrupt';
  return {
    enabled: legacy.enable ?? false,
    execution_timeout_seconds: legacy.execution_timeout_seconds ?? legacy.execution_timeout ?? legacy.timeout ?? 60,
    first_token_timeout_enabled: legacy.first_token_timeout_enabled ?? legacy.first_response_timeout_enable ?? false,
    first_token_timeout_seconds: legacy.first_token_timeout_seconds ?? legacy.first_response_timeout ?? 5,
    retry_times: legacy.retry_times ?? legacy.retry_count ?? 0,
    strategy,
    default_value: parseDefaultValue(legacy.default_assign_content),
    exception_branch_handle_id: legacy.exception_branch_handle_id || uuidv4()
  };
};
export const initExceptionHandling = target => {
  if (!target) return getDefaultExceptionHandling();
  const legacy = target.exception_handling ?? target.exception_config;
  const migrated = migrateLegacyExceptionConfig(legacy);
  target.exception_handling = {
    ...getDefaultExceptionHandling(),
    ...migrated
  };
  delete target.exception_config;
  if (!target.exception_handling.exception_branch_handle_id) {
    target.exception_handling.exception_branch_handle_id = uuidv4();
  }
  target.exception_handling.default_value = parseDefaultValue(target.exception_handling.default_value);
  return target.exception_handling;
};
export const initOutputFormat = (target, defaultValue = 'text') => {
  if (!target) return defaultValue;
  const allowed = OUTPUT_FORMAT_OPTIONS.map(item => item.value);
  if (!allowed.includes(target.output_format)) {
    target.output_format = defaultValue;
  }
  return target.output_format;
};
export const getExceptionBranchHandleId = node => node?.config?.exception_handling?.exception_branch_handle_id;
export const isExceptionBranchHandle = (handleId, node) => {
  if (!handleId || !node) return false;
  const branchHandleId = getExceptionBranchHandleId(node);
  return Boolean(branchHandleId && branchHandleId === handleId);
};
export const shouldShowExceptionBranch = node => {
  const handling = node?.config?.exception_handling;
  return handling?.enabled === true && handling?.strategy === 'exception_branch';
};
export const isExceptionEdge = edge => resolveApiEdgeType(edge) === EXCEPTION_EDGE_TYPE;
export const isEdgeFromRuntimeNode = (edge, runtimeNode, nodes = []) => {
  if (!edge || !runtimeNode) return false;
  if (edge.source === runtimeNode.id) return true;
  if (runtimeNode.blockId && edge.source === runtimeNode.blockId) return true;
  const block = nodes.find(item => item.type === 'block' && Array.isArray(item.data?.include_node_ids) && item.data.include_node_ids.includes(runtimeNode.id));
  return Boolean(block && edge.source === block.id);
};
export const resolveRuntimeNodeFromConnection = (nodes, connection) => {
  if (!connection?.source) return null;
  const sourceNode = nodes.find(item => item.id === connection.source);
  if (!sourceNode) return null;
  if (sourceNode.type !== 'block') return sourceNode;
  const childIds = sourceNode.data?.include_node_ids || [];
  const {
    sourceHandle
  } = connection;
  if (sourceHandle) {
    for (const childId of childIds) {
      const child = nodes.find(item => item.id === childId);
      if (!child) continue;
      if (sourceHandle === child.id) return child;
      if (sourceHandle === child.data?.sourceHandle) return child;
      if (isExceptionBranchHandle(sourceHandle, child)) return child;
      const llmConfig = child.config?.llm_config;
      if (llmConfig?.main_condition_id === sourceHandle) return child;
      if (llmConfig?.other_condition_id === sourceHandle) return child;
      const conditions = child.config?.if_else_conditions || [];
      if (conditions.some(item => item.condition_id === sourceHandle)) {
        return child;
      }
      const semanticConditions = [...(child.config?.semantic_conditions || []), child.config?.default_condition].filter(Boolean);
      if (semanticConditions.some(item => item.condition_id === sourceHandle)) {
        return child;
      }
    }
  }
  if (childIds.length === 1) {
    return nodes.find(item => item.id === childIds[0]) || null;
  }
  return null;
};
export const resolveRuntimeNodeFromEdge = (nodes, edge) => resolveRuntimeNodeFromConnection(nodes, edge);
export const isExceptionEdgeForRuntimeNode = (edge, runtimeNode, nodes = []) => {
  if (!edge || !runtimeNode) return false;
  const branchHandleId = getExceptionBranchHandleId(runtimeNode);
  const fromExceptionHandle = Boolean(branchHandleId && edge.sourceHandle === branchHandleId);
  if (!fromExceptionHandle && !isExceptionEdge(edge)) return false;
  return isEdgeFromRuntimeNode(edge, runtimeNode, nodes);
};
export const hasExceptionBranchEdge = (runtimeNode, edges = [], nodes = []) => edges.some(edge => isExceptionEdgeForRuntimeNode(edge, runtimeNode, nodes));
export const clampExceptionHandlingValues = handling => {
  if (!handling) return;
  const clamp = (value, fallback, min, max) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return fallback;
    return Math.min(max, Math.max(min, Math.round(num)));
  };
  handling.execution_timeout_seconds = clamp(handling.execution_timeout_seconds, 60, 0, 3600);
  handling.first_token_timeout_seconds = clamp(handling.first_token_timeout_seconds, 5, 0, 3600);
  if (handling.first_token_timeout_enabled && handling.first_token_timeout_seconds > handling.execution_timeout_seconds) {
    handling.first_token_timeout_seconds = handling.execution_timeout_seconds;
  }
  handling.retry_times = clamp(handling.retry_times, 0, 0, 3);
};
export const validateFlowExceptionHandling = (nodes = [], edges = []) => {
  const errors = [];
  nodes.forEach(node => {
    if (node.type === 'block' || node.type === 'start') {
      return;
    }
    if (node.type === 'textToImage') {
      validateTextToImageConfig(node.config).forEach(message => {
        errors.push({
          nodeId: node.id,
          message
        });
      });
    }
    const handling = node.config?.exception_handling;
    if (handling?.enabled === true && handling?.strategy === 'exception_branch') {
      const hasExceptionEdge = hasExceptionBranchEdge(node, edges, nodes);
      if (!hasExceptionEdge) {
        const nodeTitle = node.config?.title || node.config?.desc || node.data?.label || node.id;
        errors.push({
          nodeId: node.id,
          message: i18n.global.t('views.flow.helper.exceptionHandling.5df2d4f8dc227e4aace5c1c9779a68e6', [nodeTitle])
        });
      }
    }
  });
  return errors;
};
export const resolveBlockIdFromRuntimeNodeId = (nodes, nodeId) => {
  if (!nodeId || !nodes?.length) return nodeId;
  const node = nodes.find(item => item.id === nodeId);
  if (!node) return nodeId;
  if (node.type === 'block') {
    const groupedChild = (node.data?.include_node_ids || [])
      .map(childId => nodes.find(item => item.id === childId))
      .find(
        child =>
          child?.originalBlockId === node.id &&
          nodes.some(
            item =>
              item.id === child.blockId &&
              item.type === 'block' &&
              item.data?.is_group === true
          )
      );
    return groupedChild?.blockId || node.id;
  }
  if (node.blockId) return node.blockId;
  const block = nodes.find(item => item.type === 'block' && Array.isArray(item.data?.include_node_ids) && item.data.include_node_ids.includes(nodeId));
  return block?.id || nodeId;
};
export const normalizeFlowEdgeForCanvas = (edge, nodes = []) => {
  const apiEdgeType = API_EDGE_TYPES.has(edge?.type) ? edge.type : resolveApiEdgeType(edge);
  const canvasSource = resolveBlockIdFromRuntimeNodeId(nodes, edge?.source);
  const canvasTarget = resolveBlockIdFromRuntimeNodeId(nodes, edge?.target);
  const nextEdge = {
    ...edge,
    type: CANVAS_EDGE_TYPE,
    source: canvasSource,
    target: canvasTarget,
    data: buildCanvasEdgeData(apiEdgeType, edge?.data || {})
  };
  if (edge?.data?.is_group_internal === true) {
    nextEdge.hidden = true;
  }
  if (edge?.data?.edgeType === 'trigger') {
    nextEdge.selectable = false;
    nextEdge.deletable = false;
  }
  if (edge?.target && edge.target !== canvasTarget && !nextEdge.targetHandle) {
    nextEdge.targetHandle = edge.target;
  }
  return nextEdge;
};
export const normalizeFlowEdgesForCanvas = (edges = [], nodes = []) => {
  const normalizedEdges = (edges || []).map(edge =>
    normalizeFlowEdgeForCanvas(edge, nodes)
  );

  nodes
    .filter(node => node?.type === 'asyncTask' && node.data?.triggerBlockId)
    .forEach(asyncTaskNode => {
      const asyncTaskBlock = nodes.find(
        node =>
          node.id === asyncTaskNode.blockId &&
          node.data?.include_node_ids?.includes(asyncTaskNode.id)
      );
      const triggerBlock = nodes.find(
        node => node.id === asyncTaskNode.data.triggerBlockId
      );
      const triggerChildId = triggerBlock?.data?.include_node_ids?.find(
        childId => {
          const child = nodes.find(node => node.id === childId);
          return (
            child?.type === 'asyncTaskTrigger' &&
            child.data?.asyncTaskBlockId === asyncTaskBlock?.id
          );
        }
      );
      if (!asyncTaskBlock || !triggerBlock || !triggerChildId) {
        return;
      }
      const hasTriggerEdge = normalizedEdges.some(
        edge =>
          edge.source === asyncTaskBlock.id &&
          edge.sourceHandle === asyncTaskNode.data.triggerHandle &&
          edge.target === triggerBlock.id &&
          edge.targetHandle === triggerChildId
      );
      if (hasTriggerEdge) {
        return;
      }
      normalizedEdges.push({
        id: buildVueFlowEdgeId({
          source: asyncTaskBlock.id,
          sourceHandle: asyncTaskNode.data.triggerHandle,
          target: triggerBlock.id,
          targetHandle: triggerChildId
        }),
        source: asyncTaskBlock.id,
        sourceHandle: asyncTaskNode.data.triggerHandle,
        target: triggerBlock.id,
        targetHandle: triggerChildId,
        type: CANVAS_EDGE_TYPE,
        data: buildCanvasEdgeData(NORMAL_EDGE_TYPE, { edgeType: 'trigger' })
      });
    });

  return normalizedEdges;
};
const normalizeGroupedEdgeBeforeSave = (nodes, edge, runtimeSource) => {
  let changed = false;
  if (runtimeSource?.originalBlockId && edge.source === runtimeSource.blockId) {
    const sourceHandle = isExceptionBranchHandle(
      edge.sourceHandle,
      runtimeSource
    )
      ? edge.sourceHandle
      : getNodeSourceHandleIds(runtimeSource).find(
          handleId => String(handleId) === String(edge.sourceHandle)
        ) ?? getNodeSourceHandleIds(runtimeSource)[0];
    edge.source = runtimeSource.originalBlockId;
    if (sourceHandle !== undefined) {
      edge.sourceHandle = sourceHandle;
    }
    changed = true;
  }

  const runtimeTarget = nodes.find(
    node =>
      node.id === edge.targetHandle &&
      node.blockId === edge.target &&
      node.originalBlockId
  );
  if (runtimeTarget) {
    edge.target = runtimeTarget.originalBlockId;
    edge.targetHandle = runtimeTarget.id;
    changed = true;
  }

  if (changed) {
    edge.id = buildVueFlowEdgeId({
      source: edge.source,
      sourceHandle: edge.sourceHandle,
      target: edge.target,
      targetHandle: edge.targetHandle
    });
  }
};
export const normalizeFlowEdgesBeforeSave = (nodes = [], edges = []) => {
  (edges || []).forEach(edge => {
    const runtimeSource = resolveRuntimeNodeFromEdge(nodes, edge);
    normalizeGroupedEdgeBeforeSave(nodes, edge, runtimeSource);
    if (!runtimeSource) {
      if (edge.type === CANVAS_EDGE_TYPE) {
        edge.type = resolveApiEdgeType(edge);
      }
      return;
    }
    const isException = edge?.data?.isExceptionBranch === true || edge?.data?.saveEdgeType === EXCEPTION_EDGE_TYPE || isExceptionBranchHandle(edge.sourceHandle, runtimeSource);
    if (isException) {
      edge.type = EXCEPTION_EDGE_TYPE;
      edge.data = buildCanvasEdgeData(EXCEPTION_EDGE_TYPE, edge.data || {});
      if (
        edge.source !== runtimeSource.id &&
        (runtimeSource.originalBlockId ||
          isEdgeFromRuntimeNode(edge, runtimeSource, nodes))
      ) {
        edge.source = runtimeSource.id;
      }
      return;
    }
    const saveType = resolveNormalOrAsyncEdgeType(edge);
    edge.type = saveType;
    edge.data = buildCanvasEdgeData(saveType, edge.data || {});
  });
  return edges;
};
export const normalizeFlowNodesBeforeSave = (nodes = []) => {
  nodes.forEach(node => {
    if (!node?.config) return;
    initExceptionHandling(node.config);
    if (['llMReply', 'llmVariableAssignment'].includes(node.type)) {
      initOutputFormat(node.config);
      if (node.config.llm_config) {
        node.config.llm_config.enable_thinking ??= 0;
        node.config.llm_config.deep_thinking_enable ??= false;
        node.config.llm_config.deep_thinking_level ??= '';
      }
    }
  });
  return nodes;
};
export const getExceptionEdgesByNodeId = (edges, nodeId, nodes = []) => {
  const runtimeNode = nodes.find(item => item.id === nodeId);
  if (!runtimeNode) {
    return (edges || []).filter(edge => edge.source === nodeId && isExceptionEdge(edge));
  }
  return (edges || []).filter(edge => isExceptionEdgeForRuntimeNode(edge, runtimeNode, nodes));
};
export const findExceptionBranchEdge = (runtimeNodeId, edges = [], nodes = []) => {
  const runtimeNode = nodes.find(item => item.id === runtimeNodeId);
  if (!runtimeNode) return null;
  const matched = (edges || []).find(edge => isExceptionEdgeForRuntimeNode(edge, runtimeNode, nodes));
  return matched || null;
};
export const RUNTIME_EXCEPTION_STRATEGY_LABELS = {
  interrupt: i18n.global.t('views.flow.helper.exceptionHandling.1b62145849794da7c7d4080c56c795e6'),
  default_value: i18n.global.t('views.flow.helper.exceptionHandling.efc54ffea58115fb37a7e64e28b4b029'),
  exception_branch: i18n.global.t('views.flow.helper.exceptionHandling.f6a3f414c53bfd3e00719af5451bb4ae')
};
export const parseRuntimeNodeEventPayload = payload => {
  if (!payload || typeof payload !== 'object') {
    return {
      errorType: '',
      errorMessage: '',
      nodeId: '',
      nodeType: '',
      nodeTitle: '',
      attempt: 0,
      maxAttempts: 0,
      retryTimes: 0,
      strategy: '',
      timeUsedMs: 0
    };
  }
  return {
    errorType: payload.error_type ?? '',
    errorMessage: payload.error_message ?? '',
    nodeId: payload.node_id ?? '',
    nodeType: payload.node_type ?? '',
    nodeTitle: payload.node_title ?? '',
    attempt: Number(payload.attempt) || 0,
    maxAttempts: Number(payload.max_attempts) || 0,
    retryTimes: Number(payload.retry_times) || 0,
    strategy: payload.strategy ?? '',
    timeUsedMs: Number(payload.time_used_ms) || 0
  };
};
export const buildRuntimeNodeExecutionEvent = (eventType, payload = {}) => ({
  id: `${eventType}-${payload.node_id ?? 'node'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type: eventType,
  timestamp: Date.now(),
  ...parseRuntimeNodeEventPayload(payload)
});
